import type { NoteSaveEditorType } from '@/hooks/mutation/noteSaveTelemetry';
import { type SessionFlowFailure, TrackingKey } from '@heidi/runtime/performance';

import {
  classifySaveRequestFailure,
  namedError,
  type SaveRequestFailure,
  trackSaveRequestAttempt,
} from './saveRequestAttempt';

// `note_update`: one autonomous attempt per autosave the editor dispatches; a
// queued save it drops is not one. Conversion writebacks, Ask Heidi, chat and
// suggestion applies emit none; a save paused offline opens only on reconnect.

export type NoteUpdateAttempt = { editor: NoteSaveEditorType };

export type NoteUpdateErrorName =
  | 'NoteUpdateNetworkUnavailable'
  | 'NoteUpdateTimeout'
  | 'NoteUpdateServerError'
  | 'NoteUpdateRejected'
  | 'NoteUpdateForbidden'
  | 'NoteUpdateInterrupted'
  | 'NoteUpdateUnclassified';

/**
 * The `note_update` failure vocabulary, registered with the rest of the
 * session-flow names. `NoteUpdateUnclassified` is absent on purpose, so it
 * lands in `_OTHER`.
 */
export const NOTE_UPDATE_FAILURE_TYPES: Readonly<
  Record<Exclude<NoteUpdateErrorName, 'NoteUpdateUnclassified'>, SessionFlowFailure>
> = {
  NoteUpdateNetworkUnavailable: { outcome: 'failed', errorType: 'network_unavailable' },
  NoteUpdateTimeout: { outcome: 'failed', errorType: 'request_timeout' },
  NoteUpdateServerError: { outcome: 'failed', errorType: 'server_error' },
  NoteUpdateRejected: { outcome: 'failed', errorType: 'request_rejected' },
  // Lost access to the session: a precondition, not a save fault.
  NoteUpdateForbidden: { outcome: 'blocked', errorType: 'permission_denied' },
  // Session expiry or logout ended the save, not the save itself.
  NoteUpdateInterrupted: { outcome: 'cancelled' },
};

const NOTE_UPDATE_ERROR_BY_FAILURE: Readonly<
  Record<SaveRequestFailure, NoteUpdateErrorName>
> = {
  network_unavailable: 'NoteUpdateNetworkUnavailable',
  timeout: 'NoteUpdateTimeout',
  server_error: 'NoteUpdateServerError',
  rejected: 'NoteUpdateRejected',
  forbidden: 'NoteUpdateForbidden',
  interrupted: 'NoteUpdateInterrupted',
  unclassified: 'NoteUpdateUnclassified',
};

const noteUpdateError = (failure: SaveRequestFailure): Error =>
  namedError(NOTE_UPDATE_ERROR_BY_FAILURE[failure]);

/**
 * Maps a save rejection to a bounded name; an unknown cause lands in `_OTHER`.
 * The raw value is never forwarded: its name may mean something else for
 * another operation in the shared name map.
 */
export const classifyNoteUpdateError = (error: unknown): Error =>
  noteUpdateError(classifySaveRequestFailure(error));

/**
 * Runs one autosave request as a `note_update` attempt. The request's result
 * or error passes through unchanged. Instanced, so overlapping saves each
 * report their own outcome.
 */
export const trackNoteUpdateAttempt = <T>(
  attempt: NoteUpdateAttempt,
  save: () => Promise<T>
): Promise<T> =>
  trackSaveRequestAttempt(
    {
      key: TrackingKey.SCRIBE_NOTE_UPDATE,
      editor: attempt.editor,
      errorFor: noteUpdateError,
      scope: 'noteUpdateTracking',
    },
    save
  );
